import React, { useState, FormEvent, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Award,
  Send,
  CheckCircle2,
  Sparkles,
  Truck,
  Thermometer,
  Home,
  Check,
  ChevronRight,
  Facebook,
  Instagram,
  Linkedin,
  ExternalLink,
  LucideIcon,
  Link
} from 'lucide-react';
import './i18n';

interface ProductCardProps {
  icon: string;
  badge: string;
  title: string;
  desc: string;
}

interface StepCardProps {
  num: string;
  Icon: LucideIcon;
  title: string;
  desc: string;
}

interface FeatureCardProps {
  Icon: LucideIcon;
  title: string;
  desc: string;
}

export const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 4000);
  };

  return (
    <div style={styles.pageWrapper}>
      {/* Top Banner Notice */}
      <div style={styles.topNotice}>
        <span>🌿 100% Farm Fresh Delivery | Direct From Our Farm To Your Home 🥛</span>
      </div>

      {/* Navigation */}
      <header style={styles.navbar}>
        <div style={styles.navContainer}>
          <div style={styles.logoGroup}>
            <div style={styles.logoBadge}><img
              src="/logo.PNG"
              alt={t('brand')}
              style={styles.logoBadgeImage}
            /></div>
            <div>
              <h1 style={styles.logoText}>{t('brand')}</h1>
              <span style={styles.logoSub}>{t('brandSub')}</span>
            </div>
          </div>

          {/* Language Selector */}
          <div style={styles.langSelector}>
            <Globe size={16} color="#0284c7" />
            <button
              onClick={() => changeLanguage('mr')}
              style={{
                ...styles.langBtn,
                ...(i18n.language === 'mr' ? styles.langBtnActive : {})
              }}
            >
              मराठी
            </button>
            <button
              onClick={() => changeLanguage('en')}
              style={{
                ...styles.langBtn,
                ...(i18n.language === 'en' ? styles.langBtnActive : {})
              }}
            >
              English
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div style={styles.heroContent}>
          <span style={styles.heroTag}>
            <Sparkles size={14} style={{ marginRight: '6px' }} />
            Direct Farm Supply • No Middlemen
          </span>
          <h2 style={styles.heroTitle}>{t('tagline')}</h2>
          <p style={styles.heroDesc}>{t('heroDesc')}</p>
          <div style={styles.heroBtnGroup}>
            <a href="#products" style={styles.primaryBtn}>
              {t('exploreBtn')} <ChevronRight size={18} />
            </a>
            {/* <a href="#contact" style={styles.secondaryBtn}>
              {t('contactBtn')}
            </a> */}
          </div>
        </div>
      </section>

      {/* Farm About Section */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <span style={styles.sectionBadge}>Our Farm Philosophy</span>
            <h2 style={styles.sectionTitle}>{t('aboutTitle')}</h2>
            <p style={styles.sectionSubtitle}>{t('aboutSubtitle')}</p>
          </div>
          <div style={styles.aboutCard}>
            <p style={styles.aboutText}>{t('aboutText1')}</p>
            <p style={{ ...styles.aboutText, marginTop: '1rem' }}>{t('aboutText2')}</p>
          </div>
        </div>
      </section>

      {/* Process Flow - Intuitive Farm to Door */}
      <section style={{ ...styles.section, backgroundColor: '#f0f9ff' }}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={styles.sectionBadge}>Transparency First</span>
            <h2 style={styles.sectionTitle}>{t('processTitle')}</h2>
          </div>
          <div style={styles.grid4}>
            <StepCard num="1" Icon={Home} title={t('step1Title')} desc={t('step1Desc')} />
            <StepCard num="2" Icon={ShieldCheck} title={t('step2Title')} desc={t('step2Desc')} />
            {/* <StepCard num="3" Icon={Thermometer} title={t('step3Title')} desc={t('step3Desc')} /> */}
            <StepCard num="3" Icon={Truck} title={t('step4Title')} desc={t('step4Desc')} />
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" style={styles.section}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <span style={styles.sectionBadge}>Pure & Unprocessed</span>
            <h2 style={styles.sectionTitle}>{t('productsTitle')}</h2>
            <p style={styles.sectionSubtitle}>{t('productsSubtitle')}</p>
          </div>
          <div style={styles.grid4}>
            <ProductCard icon="🥛" badge="Daily Morning & Evening" title={t('milk')} desc={t('milkDesc')} />
            {/* <ProductCard icon="🧈" badge="Traditional Bilona" title={t('ghee')} desc={t('gheeDesc')} />
            <ProductCard icon="🧀" badge="100% Fresh Daily" title={t('paneer')} desc={t('paneerDesc')} />
            <ProductCard icon="🥣" badge="Probiotic Rich" title={t('curd')} desc={t('curdDesc')} /> */}
          </div>
        </div>
      </section>

      {/* Why Farm Direct */}
      <section style={{ ...styles.section, backgroundColor: '#f8fafc' }}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h2 style={styles.sectionTitle}>{t('whyTitle')}</h2>
          </div>
          <div style={styles.grid3}>
            <FeatureCard Icon={ShieldCheck} title={t('q1Title')} desc={t('q1Desc')} />
            <FeatureCard Icon={Check} title={t('q2Title')} desc={t('q2Desc')} />
            <FeatureCard Icon={Award} title={t('q3Title')} desc={t('q3Desc')} />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={styles.contactSection}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h2 style={{ ...styles.sectionTitle, color: '#ffffff' }}>{t('contactTitle')}</h2>
            <p style={{ ...styles.sectionSubtitle, color: '#94a3b8' }}>{t('contactSubtitle')}</p>
          </div>

          <div style={styles.contactGrid}>
            {/* Info Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div style={styles.infoBox}>
                <div style={styles.infoIconBg}>
                  <Phone color="#0284c7" size={20} />
                </div>
                <div>
                  <h4 style={styles.infoTitle}>{t('phone')}</h4>
                  <p style={styles.infoVal}>{t('phoneVal')}</p>
                </div>
              </div>

              <div style={styles.infoBox}>
                <div style={styles.infoIconBg}>
                  <Mail color="#0284c7" size={20} />
                </div>
                <div>
                  <h4 style={styles.infoTitle}>{t('email')}</h4>
                  <p style={styles.infoVal}>{t('emailVal')}</p>
                </div>
              </div>

              <div style={styles.infoBox}>
                <div style={styles.infoIconBg}>
                  <MapPin color="#0284c7" size={20} />
                </div>
                <div>
                  <h4 style={styles.infoTitle}>{t('address')}</h4>
                  <p style={styles.infoVal}>{t('addressVal')}</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Nirmal+Dhara+Dairy+LLP%2C+Balajinagar%2C+Bhokardan%2C+Maharashtra+431114"
                    target="_blank"
                    rel="noreferrer"
                    style={styles.mapLink}
                  >
                    <ExternalLink size={15} /> {t('viewOnMap')}
                  </a>
                </div>
              </div>
               <div style={styles.infoBox}>
                <div style={styles.infoIconBg}>
                  <Link color="#0284c7" size={20} />
                </div>
                <div>
                  <h4 style={styles.infoTitle}>{t('followUs')}</h4>
                  <div style={styles.socialList}>
                  <a
                    href="https://www.facebook.com/nirmaldharadairyfarm"
                    target="_blank"
                    rel="noreferrer"
                    style={styles.socialLink}
                  >
                    <Facebook size={18} /> Facebook
                  </a>

                  <a
                    href="https://www.instagram.com/nirmaldharadairyfarm"
                    target="_blank"
                    rel="noreferrer"
                    style={styles.socialLink}
                  >
                    <Instagram size={18} /> Instagram
                  </a>

                  <a
                    href="https://www.linkedin.com/company/nirmaldharadairy"
                    target="_blank"
                    rel="noreferrer"
                    style={styles.socialLink}
                  >
                    <Linkedin size={18} /> LinkedIn
                  </a>
                </div>
                </div>
              </div>
            </div>



            {/* Form */}
            {/* <form onSubmit={handleSubmit} style={styles.form}>
              {formSubmitted && (
                <div style={styles.alert}>
                  <CheckCircle2 size={20} /> Thank you! Our farm team will contact you shortly.
                </div>
              )}
              <div>
                <label style={styles.label}>{t('formName')}</label>
                <input required type="text" placeholder="John Doe" style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>{t('formPhone')}</label>
                <input required type="tel" placeholder="+91 9876543210" style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>{t('formMessage')}</label>
                <textarea required rows={4} placeholder="..." style={{ ...styles.input, resize: 'vertical' }}></textarea>
              </div>
              <button type="submit" style={styles.submitBtn}>
                <Send size={18} /> {t('formSubmit')}
              </button>
            </form> */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>© {new Date().getFullYear()} Nirmal Dhara Dairy LLP. {t('rights')}</p>
      </footer>
    </div>
  );
};

const ProductCard: React.FC<ProductCardProps> = ({ icon, badge, title, desc }) => (
  <div style={styles.productCard}>
    <span style={styles.productBadge}>{badge}</span>
    <div style={{ fontSize: '3rem', margin: '0.8rem 0' }}>{icon}</div>
    <h3 style={{ fontSize: '1.2rem', color: '#0f172a', marginBottom: '0.4rem', fontWeight: '700' }}>{title}</h3>
    <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>{desc}</p>
  </div>
);

const StepCard: React.FC<StepCardProps> = ({ num, Icon, title, desc }) => (
  <div style={styles.stepCard}>
    <div style={styles.stepHeader}>
      <span style={styles.stepNum}>{num}</span>
      <Icon size={24} color="#0284c7" />
    </div>
    <h3 style={{ fontSize: '1.05rem', color: '#0f172a', marginBottom: '0.4rem', fontWeight: '700' }}>{title}</h3>
    <p style={{ color: '#64748b', fontSize: '0.85rem' }}>{desc}</p>
  </div>
);

const FeatureCard: React.FC<FeatureCardProps> = ({ Icon, title, desc }) => (
  <div style={styles.featureCard}>
    <div style={styles.iconWrapper}>
      <Icon color="#0284c7" size={26} />
    </div>
    <h3 style={{ fontSize: '1.1rem', color: '#0f172a', marginBottom: '0.4rem', fontWeight: '700' }}>{title}</h3>
    <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{desc}</p>
  </div>
);

const styles: { [key: string]: CSSProperties } = {
  logoBadgeImage: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
    display: 'block',
    borderRadius: '10px'
  },
  socialCard: {
    backgroundColor: '#1e293b',
    padding: '1.2rem',
    borderRadius: '12px',
    border: '1px solid #334155'
  },
  socialList: {
    display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '0.6rem 0.75rem',
  marginTop: '0.8rem',
  width: '100%'
  },
  socialLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#f8fafc',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '600',
    flex: '1 1 120px',
    minWidth: '0'
  },
  pageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  },
  topNotice: {
    backgroundColor: '#1e293b',
    color: '#38bdf8',
    textAlign: 'center',
    padding: '6px 12px',
    fontSize: '0.82rem',
    fontWeight: '600'
  },
  navbar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  navContainer: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '0.8rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logoGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  logoBadge: {
    width: '40px',
    height: '40px',
    backgroundColor: '#e0f2fe',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.4rem'
  },
  logoText: {
    fontSize: '1.25rem',
    color: '#0284c7',
    fontWeight: '800',
    lineHeight: 1.1
  },
  logoSub: {
    fontSize: '0.72rem',
    color: '#64748b',
    letterSpacing: '1px',
    fontWeight: '700'
  },
  langSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#f1f5f9',
    padding: '4px 6px',
    borderRadius: '20px',
    border: '1px solid #cbd5e1'
  },
  langBtn: {
    border: 'none',
    background: 'transparent',
    padding: '4px 12px',
    borderRadius: '16px',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#475569',
    transition: 'all 0.2s'
  },
  langBtnActive: {
    backgroundColor: '#0284c7',
    color: '#ffffff'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    padding: '5rem 1.5rem',
    textAlign: 'center'
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  heroTag: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    color: '#0284c7',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '700',
    marginBottom: '1.2rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    border: '1px solid #bae6fd'
  },
  heroTitle: {
    fontSize: '2.6rem',
    color: '#0c4a6e',
    fontWeight: '800',
    marginBottom: '1.2rem',
    lineHeight: 1.25
  },
  heroDesc: {
    fontSize: '1.1rem',
    color: '#334155',
    marginBottom: '2rem',
    lineHeight: '1.6'
  },
  heroBtnGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    backgroundColor: '#0284c7',
    color: '#fff',
    padding: '0.8rem 1.8rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '0.98rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  secondaryBtn: {
    backgroundColor: '#ffffff',
    color: '#0284c7',
    padding: '0.8rem 1.8rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '0.98rem',
    border: '1px solid #7dd3fc',
    display: 'inline-block'
  },
  section: {
    padding: '4.5rem 1.5rem'
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto'
  },
  sectionBadge: {
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#0284c7',
    fontWeight: '700',
    marginBottom: '0.3rem',
    display: 'block'
  },
  sectionTitle: {
    fontSize: '2.1rem',
    color: '#0f172a',
    fontWeight: '800'
  },
  sectionSubtitle: {
    color: '#64748b',
    fontSize: '1rem',
    marginTop: '0.3rem'
  },
  aboutCard: {
    backgroundColor: '#ffffff',
    padding: '2.5rem',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 12px -2px rgba(0,0,0,0.03)'
  },
  aboutText: {
    fontSize: '1.05rem',
    color: '#334155',
    lineHeight: '1.8'
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '1.5rem'
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.5rem'
  },
  productCard: {
    backgroundColor: '#ffffff',
    padding: '1.8rem 1.2rem',
    borderRadius: '16px',
    textAlign: 'center',
    border: '1px solid #e2e8f0',
    position: 'relative'
  },
  productBadge: {
    backgroundColor: '#f0f9ff',
    color: '#0284c7',
    fontSize: '0.72rem',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '12px',
    border: '1px solid #bae6fd'
  },
  stepCard: {
    backgroundColor: '#ffffff',
    padding: '1.8rem 1.2rem',
    borderRadius: '16px',
    border: '1px solid #e0f2fe'
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  stepNum: {
    backgroundColor: '#0284c7',
    color: '#ffffff',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '0.85rem'
  },
  featureCard: {
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    textAlign: 'center'
  },
  iconWrapper: {
    width: '56px',
    height: '56px',
    backgroundColor: '#e0f2fe',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem auto'
  },
  contactSection: {
    padding: '4.5rem 1.5rem',
    backgroundColor: '#0f172a',
    color: '#ffffff'
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    alignItems: 'start'
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    backgroundColor: '#1e293b',
    padding: '1.2rem',
    borderRadius: '12px'
  },
  infoIconBg: {
    backgroundColor: '#0f172a',
    padding: '10px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  infoTitle: {
    fontSize: '0.95rem',
    color: '#f8fafc',
    marginBottom: '0.2rem'
  },
  infoVal: {
    fontSize: '0.88rem',
    color: '#94a3b8'
  },
  mapLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    marginTop: '0.55rem',
    color: '#38bdf8',
    textDecoration: 'none',
    fontSize: '0.82rem',
    fontWeight: '600'
  },
  form: {
    backgroundColor: '#1e293b',
    padding: '2rem',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    color: '#cbd5e1',
    marginBottom: '0.4rem',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#fff',
    fontSize: '0.95rem',
    outline: 'none'
  },
  submitBtn: {
    backgroundColor: '#0284c7',
    color: '#fff',
    padding: '0.85rem',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  alert: {
    backgroundColor: '#065f46',
    color: '#34d399',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem'
  },
  footer: {
    backgroundColor: '#020617',
    color: '#64748b',
    textAlign: 'center',
    padding: '1.5rem',
    fontSize: '0.85rem',
    borderTop: '1px solid #1e293b'
  }
};

export default App;